import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useModuleList } from '../modules/queries';
import { QualifierDetailPage } from './QualifierDetailPage';
import { useDeleteFlow } from '../../components/confirm/useDeleteFlow';
import { useConfirm } from '../../components/confirm/useConfirm';
import { useNotify } from '../../components/notifications';
import { api } from '../../api/client';
import type { ModuleParams } from '../modules/types';
import './QualifierListPage.css';

/** One qualifier row as returned by GET /api/v1/modules/qualifier. The three
 *  *Auto booleans are derived server-side (ModuleRegistry) from whether that
 *  automation group has any formula / matrix / test / method / parameter set. */
interface QRow {
  oid: string;
  qualifierId: string | null;
  symbol: string | null;
  definition: string | null;
  category: string | null;
  comment: string | null;
  retireDate: string | null;
  sampleAuto: boolean;
  qcAuto: boolean;
  surAuto: boolean;
}

type Filt = 'all' | 'active' | 'retired' | 'automated';
type Status = 'Active' | 'Retiring' | 'Retired';
// The text columns that get a G5-style header filter (▾): search box, (All),
// (Blanks), (Non blanks), then the distinct values as a checklist.
type ColKey = 'qualifierId' | 'symbol' | 'definition' | 'category' | 'comment' | 'status' | 'retire';
const COL_LABEL: Record<ColKey, string> = {
  qualifierId: 'Qualifier ID', symbol: 'Symbol', definition: 'Definition', category: 'Category',
  comment: 'Comment', status: 'Status', retire: 'Retire Date',
};

// Category → dot colour. Names come from dbo.DefinitionCategory (dynamic), so we
// key by the common ALPACA names and fall back to grey for anything else.
const CAT_COLOR: Record<string, string> = {
  'sample condition': '#d9822b',
  'holding time': '#c0392b',
  qc: '#7a4fd0',
  'reporting limit': '#2f6db0',
  result: '#1f9159',
};
const catColor = (c: string | null) => (c ? CAT_COLOR[c.trim().toLowerCase()] ?? '#c3c9d2' : '#c3c9d2');
const isAuto = (r: QRow) => !!(r.sampleAuto || r.qcAuto || r.surAuto);

const TODAY = new Date().toISOString().slice(0, 10);
// A retire date in the future ⇒ Retiring (still selectable until then); today or
// past ⇒ Retired; none ⇒ Active. Mirrors the prototype's status derivation.
const statusOf = (r: QRow): Status => {
  if (!r.retireDate) return 'Active';
  return r.retireDate.slice(0, 10) > TODAY ? 'Retiring' : 'Retired';
};
// yyyy-MM-dd(THH…) → MM/DD/YYYY for display.
const fmtDate = (d: string | null) => {
  if (!d) return '';
  const s = d.slice(0, 10);
  return `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(0, 4)}`;
};

// The filterable/sortable value for a column. Text columns read their field;
// Status is derived from the retire date, Retire Date is the formatted date
// (blank = no date), so both filter and sort on what the user actually sees.
const COL_VAL: Record<ColKey, (r: QRow) => string> = {
  qualifierId: (r) => (r.qualifierId ?? '').trim(),
  symbol: (r) => (r.symbol ?? '').trim(),
  definition: (r) => (r.definition ?? '').trim(),
  category: (r) => (r.category ?? '').trim(),
  comment: (r) => (r.comment ?? '').trim(),
  status: (r) => statusOf(r),
  retire: (r) => fmtDate(r.retireDate),
};
const cell = (r: QRow, col: ColKey) => COL_VAL[col](r);

// Pull every qualifier in one shot (small master — ~55 rows) and do search /
// sort / paginate / column-filter on the client, exactly like the approved
// prototype. This also makes the KPI counts exact (over the full set, not one page).
const ALL_PARAMS: ModuleParams = { page: 1, pageSize: 2000, search: '', rangeDays: null, filters: {} };

export function QualifierListPage() {
  const qc = useQueryClient();
  const { data, isFetching } = useModuleList('qualifier', ALL_PARAMS);
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();
  const notify = useNotify();

  const rows = (data?.data ?? []) as unknown as QRow[];

  const [q, setQ] = useState('');
  const [filt, setFilt] = useState<Filt>('all');
  const [sortK, setSortK] = useState<ColKey>('qualifierId');
  const [sortD, setSortD] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sel, setSel] = useState<Set<string>>(new Set());
  // The full-page G5-style New/Edit form (SettingsDetailPage). null = list front.
  const [detail, setDetail] = useState<{ id: string | null; mode?: 'view' | 'edit' } | null>(null);

  // Per-column header filters. Key present ⇒ that column is filtered to the
  // buckets in the Set (a bucket is the trimmed value, '' = blank). No key ⇒ the
  // column shows everything.
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  const [openCol, setOpenCol] = useState<{ col: ColKey; x: number; y: number } | null>(null);
  const [popSearch, setPopSearch] = useState('');
  const popRef = useRef<HTMLDivElement>(null);

  // Distinct buckets for a column across ALL rows (blank kept as '').
  const bucketsFor = (col: ColKey) => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(cell(r, col)));
    return [...set];
  };
  const valueOptions = (col: ColKey) =>
    bucketsFor(col).filter((v) => v !== '').sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  const hasBlank = (col: ColKey) => rows.some((r) => cell(r, col) === '');

  const isChecked = (col: ColKey, bucket: string) => {
    const f = colFilters[col];
    return f ? f.has(bucket) : true; // no filter ⇒ everything checked
  };
  const setColFilter = (col: ColKey, next: Set<string>) => {
    const all = bucketsFor(col);
    setColFilters((prev) => {
      const clone = { ...prev };
      if (next.size === all.length) delete clone[col]; // all selected ⇒ no filter
      else clone[col] = next;
      return clone;
    });
    setPage(1);
  };
  const toggleBucket = (col: ColKey, bucket: string) => {
    const base = colFilters[col] ? new Set(colFilters[col]) : new Set(bucketsFor(col));
    base.has(bucket) ? base.delete(bucket) : base.add(bucket);
    setColFilter(col, base);
  };
  const toggleAll = (col: ColKey, on: boolean) =>
    setColFilter(col, on ? new Set(bucketsFor(col)) : new Set());
  const toggleNonBlanks = (col: ColKey, on: boolean) => {
    const base = colFilters[col] ? new Set(colFilters[col]) : new Set(bucketsFor(col));
    valueOptions(col).forEach((v) => (on ? base.add(v) : base.delete(v)));
    setColFilter(col, base);
  };
  const clearCol = (col: ColKey) => { setColFilters((p) => { const c = { ...p }; delete c[col]; return c; }); setPage(1); };
  const clearAllFilters = () => { setColFilters({}); setPage(1); };
  // Active column filters, summarised for the G5-style filter bar. Each entry
  // lists the values the column is pinned to (or "N selected" past three).
  const activeFilters = Object.entries(colFilters).map(([col, set]) => {
    const vals = [...set].map((v) => (v === '' ? '(Blanks)' : v));
    return { col: col as ColKey, label: COL_LABEL[col as ColKey], desc: vals.length <= 3 ? vals.join(', ') : `${vals.length} selected` };
  });

  // Close the open header-filter popup on outside click / Escape.
  useEffect(() => {
    if (!openCol) return;
    const onDown = (e: MouseEvent) => { if (popRef.current && !popRef.current.contains(e.target as Node)) setOpenCol(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenCol(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openCol]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      active: rows.filter((r) => statusOf(r) !== 'Retired').length,
      retired: rows.filter((r) => statusOf(r) === 'Retired').length,
      automated: rows.filter(isAuto).length,
    }),
    [rows],
  );

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (filt === 'automated' && !isAuto(r)) return false;
      if (filt === 'active' && statusOf(r) === 'Retired') return false;
      if (filt === 'retired' && statusOf(r) !== 'Retired') return false;
      for (const [col, set] of Object.entries(colFilters)) {
        if (!set.has(cell(r, col as ColKey))) return false;
      }
      if (!needle) return true;
      return [r.qualifierId, r.symbol, r.definition, r.category, r.comment]
        .join(' ').toLowerCase().includes(needle);
    });
    return filtered.sort((a, b) =>
      cell(a, sortK).localeCompare(cell(b, sortK), undefined, { numeric: true, sensitivity: 'base' }) * sortD);
  }, [rows, q, filt, sortK, sortD, colFilters]);

  const pages = Math.max(1, Math.ceil(list.length / pageSize));
  const curPage = Math.min(page, pages);
  const pageRows = list.slice((curPage - 1) * pageSize, curPage * pageSize);

  const sortBy = (k: ColKey) => { setSortD(sortK === k ? -sortD : 1); setSortK(k); };
  const caret = (k: ColKey) => (k === sortK ? (sortD > 0 ? '↑' : '↓') : '↕');

  const openFilter = (col: ColKey, e: React.MouseEvent) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopSearch('');
    setOpenCol((cur) => (cur?.col === col ? null : { col, x: Math.min(r.left, window.innerWidth - 260), y: r.bottom + 4 }));
  };

  const toggle = (id: string, on: boolean) =>
    setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
  const toggleSelAll = (on: boolean) =>
    setSel((s) => { const n = new Set(s); pageRows.forEach((r) => (on ? n.add(r.oid) : n.delete(r.oid))); return n; });

  const refresh = () => qc.invalidateQueries({ queryKey: ['module', 'qualifier'] });
  const onSaved = () => { setSel(new Set()); refresh(); };

  const delSelected = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    await runDelete({
      formName: 'Qualifier',
      message: `Delete ${ids.length} qualifier${ids.length === 1 ? '' : 's'}? Qualifiers already used on results cannot be deleted.`,
      delete: async () => { for (const id of ids) await api.delete(`/api/v1/qualifiers/${id}`); },
      onDeleted: () => { setSel(new Set()); refresh(); },
    });
  };

  const retireSelected = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    const ok = await confirm({
      title: 'Retire', tone: 'default', confirmLabel: 'Retire',
      message: `Retire ${ids.length} qualifier${ids.length === 1 ? '' : 's'} as of ${fmtDate(TODAY)}? They will no longer be offered in Result Entry.`,
    });
    if (!ok) return;
    try {
      await api.post('/api/v1/qualifiers/retire', { ids, retireDate: TODAY });
      notify.success('Retire completed successfully.');
      setSel(new Set());
      refresh();
    } catch {
      notify.error('Retire failed.');
    }
  };

  const exportCsv = () => {
    const head = ['Qualifier ID', 'Symbol', 'Definition', 'Category', 'Automation', 'Comment'];
    const auto = (r: QRow) => [r.sampleAuto && 'S', r.qcAuto && 'Q', r.surAuto && 'R'].filter(Boolean).join('/');
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = list.map((r) => [r.qualifierId, r.symbol, r.definition, r.category, auto(r), r.comment].map(esc).join(','));
    const blob = new Blob([[head.map(esc).join(','), ...body].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'Qualifiers.csv'; a.click();
    URL.revokeObjectURL(a.href);
  };

  const selCount = sel.size;
  const allOnPage = pageRows.length > 0 && pageRows.every((r) => sel.has(r.oid));

  // Column header with sort + G5-style filter chevron.
  const FilterHead = ({ col, label }: { col: ColKey; label: string }) => (
    <th className="qz-fh">
      <span className="qz-hl" onClick={() => sortBy(col)}>{label}<span className="qz-caret">{caret(col)}</span></span>
      <button type="button" className={`qz-fbtn${colFilters[col] ? ' on' : ''}`}
        title="Filter" onClick={(e) => openFilter(col, e)}>▾</button>
    </th>
  );

  // New / Edit → the shared full-page detail form (SettingsDetailPage), the SAME
  // one the generic list uses, so it matches the legacy G5 layout exactly:
  // Definition + Sample / QC / Surrogate Automation sections with the cascading
  // Matrix → Test → Method → Parameter multi-selects.
  if (detail) {
    return (
      <QualifierDetailPage
        id={detail.id}
        siblingIds={list.map((r) => r.oid)}
        onNavigate={(nid) => setDetail({ id: nid, mode: detail.mode })}
        onClose={() => setDetail(null)}
        onSaved={onSaved}
      />
    );
  }

  return (
    <div className="qzp">
      <div className="qz-titlerow">
        <div className="qz-ftic">Q</div>
        <h1>Qualifier</h1>
        <span className="qz-loadchip">{isFetching ? 'Loading…' : `${rows.length} qualifiers`}</span>
      </div>
      <div className="qz-crumbs">
        Home<span className="sep">›</span>Document Management<span className="sep">›</span>
        <b style={{ color: 'var(--qz-ink)' }}>Qualifier</b>
      </div>

      <div className="qz-toolbar">
        <button className="qz-ca qz-ghost" disabled={!selCount} onClick={retireSelected}
          title="Retire the selected qualifier(s) as of today">
          <i className="qz-gi rt">⊘</i> Retire
        </button>
        <span className="qz-vsep" />
        <button className="qz-ca" onClick={() => setDetail({ id: null, mode: 'edit' })}>
          <i className="qz-gi sv">＋</i> New
        </button>
        <button className="qz-ca qz-ghost" disabled={selCount !== 1}
          onClick={() => { const only = [...sel][0]; if (only) setDetail({ id: only, mode: 'edit' }); }}>
          <i className="qz-gi ed">✎</i> Edit
        </button>
        <button className="qz-ca qz-ghost" disabled={!selCount} onClick={delSelected}>
          <i className="qz-gi dl">✕</i> Delete
        </button>
        <button className="qz-ca qz-ghost" onClick={refresh}><i className="qz-gi rf">⟳</i> Refresh</button>
        <button className="qz-ca qz-ghost" onClick={exportCsv}><i className="qz-gi ex">⤓</i> Export</button>
        <span className="qz-vsep" />
        <button className="qz-ca qz-ghost"
          onClick={() => window.open('/m/audittrail', '_self')}><i className="qz-gi au">⛨</i> Audit</button>
        <span className="qz-sp" />
        <input className="qz-search" placeholder="Search ID, symbol, definition…"
          value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      </div>

      <div className="qz-kpis">
        {([
          ['all', 'All qualifiers', 'Q', '#e8670a', '#fff1e6', counts.all],
          ['active', 'Active', '✓', '#1f9159', '#e6f4ec', counts.active],
          ['retired', 'Retired', '⊘', '#6b7280', '#eef0f3', counts.retired],
          ['automated', 'Automated', '⚡', '#7a4fd0', '#f2edfc', counts.automated],
        ] as [Filt, string, string, string, string, number][]).map(([k, label, ic, c, b, n]) => (
          <div key={k} className={`qz-kpi${filt === k ? ' on' : ''}`}
            style={{ ['--qz-k' as string]: c, ['--qz-kb' as string]: b }}
            onClick={() => { setFilt(k); setPage(1); }}>
            <span className="qz-ki">{ic}</span>
            <span className="qz-kl">{label}</span>
            <span className="qz-kn">{n}</span>
          </div>
        ))}
      </div>

      <div className="qz-card">
        <div className="qz-scroll">
          <table>
            <thead>
              <tr>
                <th className="qz-cb"><input type="checkbox" checked={allOnPage}
                  onChange={(e) => toggleSelAll(e.target.checked)} /></th>
                <th className="qz-ed" />
                <FilterHead col="qualifierId" label="Qualifier ID" />
                <FilterHead col="symbol" label="Symbol" />
                <FilterHead col="definition" label="Definition" />
                <FilterHead col="category" label="Category" />
                <th title="Auto-applied by Sample / QC / Surrogate rules">Automation</th>
                <FilterHead col="status" label="Status" />
                <FilterHead col="retire" label="Retire Date" />
                <FilterHead col="comment" label="Comment" />
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr><td colSpan={10} className="qz-empty">{isFetching ? 'Loading…' : 'No qualifiers match.'}</td></tr>
              ) : pageRows.map((r) => {
                const st = statusOf(r);
                const stripe = st === 'Retired' ? 'st-off' : st === 'Retiring' ? 'st-warn' : 'st-ok';
                return (
                <tr key={r.oid} className={`${stripe}${st === 'Retired' ? ' retired' : ''}${sel.has(r.oid) ? ' sel' : ''}`}
                  onDoubleClick={() => setDetail({ id: r.oid, mode: 'edit' })}>
                  <td className="qz-cb"><input type="checkbox" checked={sel.has(r.oid)}
                    onChange={(e) => toggle(r.oid, e.target.checked)} /></td>
                  <td className="qz-ed"><span title="Edit" onClick={() => setDetail({ id: r.oid, mode: 'edit' })}>✎</span></td>
                  <td className="qz-nw">{r.qualifierId}</td>
                  <td className="qz-nw"><span className="qz-sym">{r.symbol}</span></td>
                  <td className="qz-def">{r.definition}</td>
                  <td className="qz-nw">
                    {r.category
                      ? <span className="qz-cat" style={{ ['--qz-cd' as string]: catColor(r.category) }}>{r.category}</span>
                      : ''}
                  </td>
                  <td className="qz-nw">
                    <span className="qz-auto">
                      <i className={`S${r.sampleAuto ? ' y' : ''}`} title="Sample">S</i>
                      <i className={`Q${r.qcAuto ? ' y' : ''}`} title="QC">Q</i>
                      <i className={`R${r.surAuto ? ' y' : ''}`} title="Surrogate">R</i>
                    </span>
                  </td>
                  <td className="qz-nw">
                    <span className={`qz-pill ${st === 'Active' ? 'ok' : st === 'Retiring' ? 'warn' : 'off'}`}>
                      {st === 'Retiring' ? `Retiring ${fmtDate(r.retireDate).slice(0, 5)}` : st}
                    </span>
                  </td>
                  <td className="qz-nw">{fmtDate(r.retireDate)}</td>
                  <td>{r.comment}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="qz-footer">
          <span>Page {curPage} of {pages} ({list.length} items)</span>
          <span className="qz-pg">
            <span className="qz-p" onClick={() => setPage(Math.max(1, curPage - 1))}>‹</span>
            {Array.from({ length: pages }, (_, i) => i + 1).map((i) => (
              <span key={i} className={`qz-p${i === curPage ? ' cur' : ''}`} onClick={() => setPage(i)}>{i}</span>
            ))}
            <span className="qz-p" onClick={() => setPage(Math.min(pages, curPage + 1))}>›</span>
          </span>
          <span>Page size:{' '}
            <select value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
              <option>10</option><option>20</option><option>50</option><option>100</option>
            </select>
          </span>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="qz-filterbar">
          <input type="checkbox" checked readOnly className="qz-fbcheck" title="Filter active" />
          <span className="qz-fbicon">⧩</span>
          {activeFilters.map((f) => (
            <span key={f.col} className="qz-fbchip">
              <b>{f.label}</b>: {f.desc}
              <button type="button" className="qz-fbx" title="Remove this filter" onClick={() => clearCol(f.col)}>×</button>
            </span>
          ))}
          <span className="qz-fbsp" />
          <button type="button" className="qz-fbclear" onClick={clearAllFilters}>Clear</button>
        </div>
      )}

      {openCol && (() => {
        const col = openCol.col;
        const opts = valueOptions(col).filter((v) => !popSearch || v.toLowerCase().includes(popSearch.toLowerCase()));
        const allChecked = !colFilters[col];
        const nonBlankChecked = valueOptions(col).length > 0 && valueOptions(col).every((v) => isChecked(col, v));
        return (
          <div ref={popRef} className="qz-fpop" style={{ left: openCol.x, top: openCol.y }}>
            <input className="qz-fsearch" autoFocus placeholder="Enter text to filter…"
              value={popSearch} onChange={(e) => setPopSearch(e.target.value)} />
            <div className="qz-flist">
              {/* Meta rows — ALWAYS visible, INCLUDING while a search term is typed,
                  so the user can still (un)check (All) / (Blanks) / (Non blanks)
                  after filtering the list (owner request 2026-10-05; previously
                  gated on !popSearch, so they vanished the moment you typed). They
                  operate on the WHOLE value set, not just the search-filtered rows.
                  (Blanks) still shows only when the column actually has empty cells. */}
              <label className="qz-fopt"><input type="checkbox" checked={allChecked}
                onChange={(e) => toggleAll(col, e.target.checked)} /><span>(All)</span></label>
              {hasBlank(col) && (
                <label className="qz-fopt"><input type="checkbox" checked={isChecked(col, '')}
                  onChange={() => toggleBucket(col, '')} /><span>(Blanks)</span></label>
              )}
              <label className="qz-fopt"><input type="checkbox" checked={nonBlankChecked}
                onChange={(e) => toggleNonBlanks(col, e.target.checked)} /><span>(Non blanks)</span></label>
              <div className="qz-fsep" />
              {opts.length === 0 ? (
                <div className="qz-fnone">No matches</div>
              ) : opts.map((v) => (
                <label key={v} className="qz-fopt" title={v}>
                  <input type="checkbox" checked={isChecked(col, v)} onChange={() => toggleBucket(col, v)} />
                  <span>{v}</span>
                </label>
              ))}
            </div>
            <div className="qz-ffoot">
              <button type="button" className="qz-flink" onClick={() => clearCol(col)}>Clear</button>
              <button type="button" className="qz-ca qz-ghost qz-fclose" onClick={() => setOpenCol(null)}>Close</button>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
